// src/features/tidsregistering/components/attendance/useHourViewLogic.js
import { useRef, useState, useCallback, useMemo } from 'react';

/**
 * Manages scroll state and coordinate math for the hour-granularity timeline.
 *
 * Treats each 15-minute slot as a "column" — the same unit as a "day" in the
 * regular gantt. This lets TimelineGrid handle virtual scrolling unchanged.
 *
 * Returns:
 *   scrollContainerRef  — attach to TimelineGrid ref
 *   scrollState         — { scrollLeft, timelineStart, timelineEnd }
 *   handleScroll        — onScroll handler
 *   baseDate            — Date anchored to midnight of selectedDate
 *   timeToX(Date)       — pixel offset from the left edge of the full timeline
 *   hourGroups          — [{ hour, label, startSlot, slotCount }] for HourViewHeader
 *   SLOT_WIDTH          — px per slot column
 *   SLOT_MINUTES        — minutes per slot column
 *   totalSlots          — total number of slot columns
 */
export function useHourViewLogic({
  selectedDate,
  dayStartHour = 0,
  dayEndHour = 24,
  slotMinutes = 15,
  slotWidthPx = 30,
}) {
  const scrollContainerRef = useRef(null);
  const [scrollLeft, setScrollLeft] = useState(0);

  const totalSlots = ((dayEndHour - dayStartHour) * 60) / slotMinutes;
  const minuteWidthPx = slotWidthPx / slotMinutes;

  const handleScroll = useCallback((e) => {
    setScrollLeft(e.currentTarget.scrollLeft);
  }, []);

  const scrollState = useMemo(
    () => ({ scrollLeft, timelineStart: 0, timelineEnd: totalSlots - 1 }),
    [scrollLeft, totalSlots],
  );

  const timeToX = useCallback(
    (date) => {
      const minutesFromStart = (date.getHours() - dayStartHour) * 60 + date.getMinutes();
      return Math.max(0, minutesFromStart * minuteWidthPx);
    },
    [dayStartHour, minuteWidthPx],
  );

  const hourGroups = useMemo(() => {
    const slotsPerHour = 60 / slotMinutes;
    const groups = [];
    for (let h = dayStartHour; h < dayEndHour; h++) {
      groups.push({
        hour: h,
        label: String(h).padStart(2, '0'),
        startSlot: (h - dayStartHour) * slotsPerHour,
        slotCount: slotsPerHour,
      });
    }
    return groups;
  }, [dayStartHour, dayEndHour, slotMinutes]);

  const baseDate = useMemo(() => new Date(`${selectedDate}T00:00:00`), [selectedDate]);

  // Converts an absolute slot index to a Date with the correct time component
  const slotIndexToTime = useCallback(
    (idx) => {
      const minutesFromMidnight = dayStartHour * 60 + idx * slotMinutes;
      const d = new Date(`${selectedDate}T00:00:00`);
      d.setHours(Math.floor(minutesFromMidnight / 60), minutesFromMidnight % 60, 0, 0);
      return d;
    },
    [selectedDate, dayStartHour, slotMinutes],
  );

  return {
    scrollContainerRef,
    scrollState,
    handleScroll,
    baseDate,
    timeToX,
    slotIndexToTime,
    hourGroups,
    SLOT_WIDTH: slotWidthPx,
    SLOT_MINUTES: slotMinutes,
    totalSlots,
  };
}
